import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AvailableFields, FieldDefinition } from '../types';
import { getAvailableFields, isAuthenticated } from '../api';
import { useSettings } from './SettingsContext';

interface FieldVisibilityContextType {
  availableFields: AvailableFields | null;
  loading: boolean;
  error: string | null;
  isFieldVisible: (fieldKey: string) => boolean;
  getVisibleOptionalFields: () => FieldDefinition[];
  getMandatoryFields: () => FieldDefinition[];
  getAllFields: () => FieldDefinition[];
  refreshAvailableFields: () => Promise<void>;
  refreshAfterLogin: () => Promise<void>;
}

const FieldVisibilityContext = createContext<FieldVisibilityContextType | undefined>(undefined);

export const useFieldVisibility = () => {
  const context = useContext(FieldVisibilityContext);
  if (context === undefined) {
    throw new Error('useFieldVisibility must be used within a FieldVisibilityProvider');
  }
  return context;
};

interface FieldVisibilityProviderProps {
  children: ReactNode;
}

export const FieldVisibilityProvider: React.FC<FieldVisibilityProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [availableFields, setAvailableFields] = useState<AvailableFields | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { settings } = useSettings();

  const refreshAvailableFields = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fields = await getAvailableFields();
      setAvailableFields(fields);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.failedToLoadAvailableFields');
      setError(errorMessage);
      
    } finally {
      setLoading(false);
    }
  }, [t]);

  const refreshAfterLogin = useCallback(async () => {
    // Force refresh after login
    await refreshAvailableFields();
  }, [refreshAvailableFields]);

  // Get the user's visible field preferences, defaulting to all fields visible
  const getVisibleFieldKeys = useCallback((): string[] => {
    if (!settings?.visible_fields) {
      // Default: all optional fields are visible (only when no preferences exist)
      return availableFields?.optional_fields.map(f => f.key) || [];
    }
    // Return user's preferences (even if empty array - user wants no fields visible)
    return settings.visible_fields;
  }, [settings?.visible_fields, availableFields?.optional_fields]);

  const isFieldVisible = useCallback((fieldKey: string): boolean => {
    if (!availableFields) return true; // Default to visible while loading

    // Mandatory fields are always visible
    const isMandatory = availableFields.mandatory_fields.some(f => f.key === fieldKey);
    if (isMandatory) return true;

    // Check if optional field is in the visible list
    const visibleKeys = getVisibleFieldKeys();
    return visibleKeys.includes(fieldKey);
  }, [availableFields, getVisibleFieldKeys]);

  const getVisibleOptionalFields = useCallback((): FieldDefinition[] => {
    if (!availableFields) return [];
    
    const visibleKeys = getVisibleFieldKeys();
    return availableFields.optional_fields.filter(field => visibleKeys.includes(field.key));
  }, [availableFields, getVisibleFieldKeys]);

  const getMandatoryFields = useCallback((): FieldDefinition[] => {
    return availableFields?.mandatory_fields || [];
  }, [availableFields]);

  const getAllFields = useCallback((): FieldDefinition[] => {
    if (!availableFields) return [];
    return [...availableFields.mandatory_fields, ...availableFields.optional_fields];
  }, [availableFields]);

  // Load available fields on mount, but only if authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      refreshAvailableFields();
    }
  }, [refreshAvailableFields]);

  const value: FieldVisibilityContextType = {
    availableFields,
    loading,
    error,
    isFieldVisible,
    getVisibleOptionalFields,
    getMandatoryFields,
    getAllFields,
    refreshAvailableFields,
    refreshAfterLogin,
  };

  return <FieldVisibilityContext.Provider value={value}>{children}</FieldVisibilityContext.Provider>;
};